// El CONTROLADOR que `InterfazV2` registra en el puente mientras tiene un modo abierto —
// ADR-2026-09-10-el-agente-toca-la-pantalla.
//
// Recibe el estado vivo del modo y sus setters (los mismos que usan los paneles), arma
// la ficha con `describirModo` y traduce cada cambio validado a la llamada que haría un
// click en la interfaz. Nada de acá escribe estado por su cuenta: `productos` pasa por
// el mismo `handleConfirmProducts` que el picker, `poses` por `handlePoseIdsChange`, el
// modelo por `handleModelChangeSingle`, etc. Si mañana un setter cambia de reglas, el
// chat las hereda.
//
// El hook se llama en cada render (la ficha se arma con los valores de ese render) y
// registra una sola vez, vía `useRegistrarPantalla`. Con el modo cerrado registra null.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRegistrarPantalla } from '../../../chat-atlas/PuenteDePantallaContext';
import type {
  AplicacionDeCambios,
  CambioValidado,
  ControladorDePantalla,
} from '../../../chat-atlas/puenteDePantalla';
import type { ResolucionImagen } from '../../../lib/resolucionImagen';
import type { AnuncioFieldState } from '../panels/AnuncioPanel';
import type { VoiceGuionValue } from '../components/VoiceGuionSection';
import type { FondoSelection } from '../FondoPicker';
import type { ModelSelection } from '../ModelPicker';
import type { MockProductV2 } from '../mockData';
import type { ModelV2 } from '../modelsApi';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import { fetchVideoHooks, type GanchoOption } from '../ganchosCatalog';
import { fetchGoogleVoices, type GoogleVoice } from '../googleVoicesApi';
import { describirModo, KINDS_DE_PLANTILLA, TOPES, type DatosDelModo, modoUsaColorLiso } from './camposDeModo';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

export interface EstadoDelModo {
  /** El `kind` del preset abierto, o null con el sheet cerrado. */
  kind: string | null;
  plantillaTitulo?: string;
  ruta: string;
  paso: string;
  productos: MockProductV2[];
  catalogo: MockProductV2[];
  sinProducto: boolean;
  modelo: ModelSelection | null;
  modelos: ModelV2[];
  poses: string[];
  posesDisponibles: PoseV2[];
  posesPropias: PoseV2[];
  variasPoses: boolean;
  fondo: FondoSelection | null;
  fondosBiblioteca: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  formato: string | null;
  resolucion: ResolucionImagen;
  /** La plantilla abierta elige Calidad — ver `DatosDelModo.eligeCalidad`. */
  eligeCalidad?: boolean;
  instruccion: string;
  cantidad: number;
  cantidadMax: number;
  vistas: string[];
  vistasDisponibles: { id: string; nombre: string; productIds: string[] }[];
  anuncio: {
    titulo: AnuncioFieldState;
    descripcion: AnuncioFieldState;
    cta: AnuncioFieldState;
    colores: string[];
    coloresMarca: string[];
    coloresMax: number;
  };
  video: {
    duracion: number;
    min: number;
    max: number;
    multiEscena: boolean;
    ganchoId: string | null;
    voz: VoiceGuionValue;
  };
  costo?: string;
}

export interface AccionesDelModo {
  confirmarProductos: (productos: MockProductV2[]) => void;
  sinProducto: () => void;
  cambiarModelo: (sel: ModelSelection) => void;
  cambiarPoses: (ids: string[]) => void;
  cambiarVariasPoses: (v: boolean) => void;
  cambiarFondo: (sel: FondoSelection) => void;
  cambiarFormato: (id: string) => void;
  cambiarResolucion: (r: ResolucionImagen) => void;
  cambiarInstruccion: (t: string) => void;
  cambiarCantidad: (n: number) => void;
  cambiarVistas: (ids: string[]) => void;
  cambiarTitulo: (f: (prev: AnuncioFieldState) => AnuncioFieldState) => void;
  cambiarDescripcion: (f: (prev: AnuncioFieldState) => AnuncioFieldState) => void;
  cambiarCta: (f: (prev: AnuncioFieldState) => AnuncioFieldState) => void;
  cambiarColores: (c: string[]) => void;
  cambiarDuracion: (n: number) => void;
  cambiarMultiEscena: (v: boolean) => void;
  cambiarGancho: (id: string | null) => void;
  cambiarVoz: (f: (prev: VoiceGuionValue) => VoiceGuionValue) => void;
}

const HEX = /^#[0-9a-f]{6}$/i;

/** "#1644EB, #fff" → ["#1644EB", "#FFFFFF"], hasta el tope; lo que no es hex se ignora. */ // hex-de-marca-ok: ejemplo de hex en un texto
export function coloresDesdeTexto(t: string, max: number): string[] {
  const salida: string[] = [];
  for (const crudo of t.split(/[,;\s]+/)) {
    let c = crudo.trim();
    if (!c) continue;
    if (!c.startsWith('#')) c = `#${c}`;
    if (/^#[0-9a-f]{3}$/i.test(c)) c = `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    if (!HEX.test(c)) continue;
    const norm = c.toUpperCase();
    if (!salida.includes(norm)) salida.push(norm);
    if (salida.length >= max) break;
  }
  return salida;
}

/**
 * Aplica un cambio ya validado con las acciones del modo. Devuelve null si quedó, o el
 * motivo si en el estado actual no se pudo (por ejemplo, un producto que ya no está).
 */
export function aplicarCambioEnModo(
  c: CambioValidado,
  e: EstadoDelModo,
  a: AccionesDelModo,
): string | null {
  switch (c.campo) {
    case 'productos': {
      const ids = c.valor as string[];
      const porId = new Map(e.catalogo.map((p) => [p.id, p]));
      const elegidos = ids.map((id) => porId.get(id)).filter((p): p is MockProductV2 => !!p);
      if (elegidos.length !== ids.length) return 'alguno de esos productos ya no está en el catálogo.';
      a.confirmarProductos(elegidos);
      return null;
    }
    case 'sin_producto': {
      if (c.valor === true) a.sinProducto();
      else return 'para volver a usar un producto, mandá `productos`.';
      return null;
    }
    case 'modelo': {
      const id = c.valor as string;
      if (id === 'auto') {
        a.cambiarModelo({ mode: 'auto' });
        return null;
      }
      const m = e.modelos.find((x) => String(x.id) === id);
      if (!m) return `ese ${palabraDeModeloActual().singular} ya no está en la lista.`;
      // La misma forma que arma el picker al elegirlo de la biblioteca.
      a.cambiarModelo({ mode: 'preset', id: String(m.id), name: m.nombre, imageUrl: m.thumbnailUrl });
      return null;
    }
    case 'poses': {
      const ids = c.valor as string[];
      if (ids.length > 1 && !e.variasPoses) a.cambiarVariasPoses(true);
      a.cambiarPoses(ids);
      return null;
    }
    case 'fondo': {
      const v = c.valor as string;
      if (e.kind === 'fotos-producto') {
        a.cambiarFondo({ mode: 'blanco', colorLiso: v as 'blanco' | 'beige' | 'rosa' | 'negro' });
        return null;
      }
      if (v === 'auto') a.cambiarFondo({ mode: 'auto' });
      else if (v === 'blanco') a.cambiarFondo({ mode: 'blanco' });
      else if (v.startsWith('liso:')) {
        // El liso con color es la MISMA selección que el blanco, con `colorLiso`: el
        // armado del payload lo rutea a descripción
        // (ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo).
        //
        // Se valida acá también y no sólo en la lista de opciones: si el agente inventa
        // un `liso:` en un modo que no lo consume (muebles), confirmarlo sería mentirle
        // al usuario con un color que la generación descarta.
        if (!modoUsaColorLiso(e.kind)) return 'este modo no tiene fondo liso de color.';
        a.cambiarFondo({
          mode: 'blanco',
          colorLiso: v.slice('liso:'.length) as 'beige' | 'rosa' | 'negro',
        });
      }
      else if (v.startsWith('biblioteca:')) {
        const id = Number(v.slice('biblioteca:'.length));
        const f = e.fondosBiblioteca.find((x) => x.id === id);
        if (!f) return 'ese fondo ya no está en la biblioteca.';
        a.cambiarFondo({ mode: 'biblioteca', id: f.id, name: f.nombre, imageUrl: f.imageUrl });
      } else if (v.startsWith('guardado:')) {
        const fileName = v.slice('guardado:'.length);
        const f = e.fondosGuardados.find((x) => x.fileName === fileName);
        if (!f) return 'ese fondo propio ya no está.';
        a.cambiarFondo({ mode: 'guardado', fileName: f.fileName, name: f.nombre, imageUrl: f.imageUrl });
      } else return 'fondo desconocido.';
      return null;
    }
    case 'fondo_descripcion': {
      const t = c.valor as string;
      if (!t) {
        a.cambiarFondo(e.kind === 'fotos-producto' ? { mode: 'blanco' } : { mode: 'auto' });
      } else a.cambiarFondo({ mode: 'descripcion', text: t });
      return null;
    }
    case 'vistas':
      a.cambiarVistas(c.valor as string[]);
      return null;
    case 'formato':
      a.cambiarFormato(c.valor as string);
      return null;
    case 'resolucion':
      a.cambiarResolucion(c.valor as ResolucionImagen);
      return null;
    case 'instruccion':
      a.cambiarInstruccion(c.valor as string);
      return null;
    case 'cantidad':
      a.cambiarCantidad(c.valor as number);
      return null;
    case 'titulo':
      a.cambiarTitulo((prev) => ({ ...prev, value: c.valor as string, hidden: false }));
      return null;
    case 'sin_titulo':
      a.cambiarTitulo((prev) => ({ ...prev, hidden: c.valor as boolean }));
      return null;
    case 'descripcion':
      a.cambiarDescripcion((prev) => ({ ...prev, value: c.valor as string, hidden: false }));
      return null;
    case 'sin_descripcion':
      a.cambiarDescripcion((prev) => ({ ...prev, hidden: c.valor as boolean }));
      return null;
    case 'cta':
      a.cambiarCta((prev) => ({ ...prev, value: c.valor as string, hidden: false }));
      return null;
    case 'sin_cta':
      a.cambiarCta((prev) => ({ ...prev, hidden: c.valor as boolean }));
      return null;
    case 'colores': {
      const t = c.valor as string;
      const colores = coloresDesdeTexto(t, e.anuncio.coloresMax);
      if (t.trim() && colores.length === 0) return 'ningún color válido: mandalos en hex, como #1644EB.'; // hex-de-marca-ok: ejemplo de hex en un texto
      a.cambiarColores(colores);
      return null;
    }
    case 'duracion':
      a.cambiarDuracion(c.valor as number);
      return null;
    case 'multi_escena':
      a.cambiarMultiEscena(c.valor as boolean);
      return null;
    case 'gancho':
      a.cambiarGancho(c.valor as string);
      return null;
    case 'voz': {
      const nombre = c.valor as string;
      a.cambiarVoz((prev) => ({ ...prev, vozId: nombre, vozNombre: nombre }));
      return null;
    }
    case 'forma_de_hablar':
      a.cambiarVoz((prev) => ({ ...prev, formaDeHablar: c.valor as string }));
      return null;
    case 'guion': {
      const t = c.valor as string;
      a.cambiarVoz((prev) =>
        t ? { ...prev, guionMode: 'texto', guionTexto: t } : { ...prev, guionTexto: '' },
      );
      return null;
    }
    case 'subtitulos':
      a.cambiarVoz((prev) => ({ ...prev, subtitulos: c.valor as boolean }));
      return null;
    default:
      return 'campo desconocido.';
  }
}

/** Los kinds que abren en el sheet de InterfazV2 y tienen ficha. */
const KINDS_CON_CONTROLADOR = new Set([
  'design-modelo',
  'design-auto',
  'fotos-producto',
  'anuncio',
  'catalogo-ia',
  'infografia',
  'video-influencer',
]);

/**
 * Registra el controlador del modo abierto. Se llama en cada render de InterfazV2 con
 * el estado vigente; con el sheet cerrado (o un kind sin ficha) registra null.
 */
export function useControladorDelModo(estado: EstadoDelModo, acciones: AccionesDelModo): void {
  const kind = estado.kind;
  const tieneFicha = !!kind && (KINDS_CON_CONTROLADOR.has(kind) || KINDS_DE_PLANTILLA.has(kind));

  // Los ganchos y las voces del modo Video viven adentro de sus secciones; para la ficha
  // se traen acá una vez, cuando el modo se abre. Sin ellos, esos dos campos son de solo
  // lectura hasta que lleguen.
  const [ganchos, setGanchos] = useState<GanchoOption[] | null>(null);
  const [voces, setVoces] = useState<GoogleVoice[] | null>(null);
  useEffect(() => {
    if (kind !== 'video-influencer' || (ganchos && voces)) return;
    let vivo = true;
    void fetchVideoHooks().then((g) => vivo && setGanchos(g)).catch(() => vivo && setGanchos([]));
    void fetchGoogleVoices().then((v) => vivo && setVoces(v)).catch(() => vivo && setVoces([]));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  // Las acciones y el estado del ÚLTIMO render, para que `aplicar` (que corre después,
  // desde el chat) no cierre sobre un render viejo.
  const estadoRef = useRef(estado);
  estadoRef.current = estado;
  const accionesRef = useRef(acciones);
  accionesRef.current = acciones;
  const ganchosRef = useRef(ganchos);
  ganchosRef.current = ganchos;
  const vocesRef = useRef(voces);
  vocesRef.current = voces;

  const controlador = useMemo<ControladorDePantalla | null>(() => {
    if (!tieneFicha || !kind) return null;
    const describir = () => {
      const e = estadoRef.current;
      const g = ganchosRef.current;
      const v = vocesRef.current;
      const datos: DatosDelModo = {
        kind: e.kind ?? kind,
        ruta: e.ruta,
        paso: e.paso,
        plantillaTitulo: e.plantillaTitulo,
        productos: e.productos.map((p) => ({ id: p.id, name: p.name })),
        catalogo: e.catalogo.map((p) => ({ id: p.id, name: p.name })),
        sinProducto: e.sinProducto,
        modelo: e.modelo,
        modelos: e.modelos.map((m) => ({ id: m.id, nombre: m.nombre })),
        poses: e.poses,
        posesDisponibles: [...e.posesDisponibles, ...e.posesPropias].map((p) => ({
          id: p.id,
          nombre: p.nombre,
          cantidadPersonas: p.cantidadPersonas,
        })),
        fondo: e.fondo,
        fondosBiblioteca: e.fondosBiblioteca.map((f) => ({ id: f.id, nombre: f.nombre, categoria: f.categoria })),
        fondosGuardados: e.fondosGuardados.map((f) => ({ fileName: f.fileName, nombre: f.nombre })),
        formato: e.formato,
        resolucion: e.resolucion,
        eligeCalidad: e.eligeCalidad,
        instruccion: e.instruccion,
        cantidad: e.cantidad,
        cantidadMax: e.cantidadMax,
        vistas: e.vistas,
        vistasDisponibles: e.vistasDisponibles,
        anuncio: e.anuncio,
        video: {
          duracion: e.video.duracion,
          min: e.video.min,
          max: e.video.max,
          multiEscena: e.video.multiEscena,
          ganchoId: e.video.ganchoId,
          ganchos: (g ?? []).map((x) => ({ id: x.id, nombre: x.nombre })),
          ganchosCargados: g !== null,
          voz: e.video.voz,
          voces: (v ?? []).map((x) => ({ nombre: x.nombre, estilo: x.estilo, sexo: x.sexo })),
          vocesCargadas: v !== null,
        },
        costo: e.costo,
      };
      return describirModo(datos);
    };
    const aplicar = (cambios: CambioValidado[]): AplicacionDeCambios => {
      const aplicados: string[] = [];
      const fallidos: { campo: string; motivo: string }[] = [];
      // `productos` primero: en el paso de elegir producto, ponerlos abre el configure y
      // los demás campos recién ahí tienen dónde caer.
      const ordenados = [...cambios].sort((x, y) => (x.campo === 'productos' ? -1 : y.campo === 'productos' ? 1 : 0));
      for (const c of ordenados) {
        const motivo = aplicarCambioEnModo(c, estadoRef.current, accionesRef.current);
        if (motivo) fallidos.push({ campo: c.campo, motivo });
        else aplicados.push(c.campo);
      }
      return { aplicados, fallidos };
    };
    return { clave: `modo:${kind}`, describir, aplicar };
  }, [kind, tieneFicha]);

  useRegistrarPantalla(controlador);
}

export { TOPES };
